import { UnorderedListOutlined, CheckCircleOutlined, ClockCircleOutlined, CreditCardOutlined, FileTextOutlined, FormOutlined, FundOutlined, HeartOutlined, HistoryOutlined, IdcardOutlined, LockOutlined, LoginOutlined, PlusOutlined, RiseOutlined, SaveOutlined, ScanOutlined, SnippetsOutlined, UnlockOutlined, WalletOutlined, WarningOutlined } from "@ant-design/icons";
import { Alert, Button, Input, Select, Space } from "../../../../../../../shared/frontend/ui";
import type { App as AntdApp } from "../../../../../../../shared/frontend/ui";
import { DashboardGrid, type DashboardCardMeta } from "../../../../../../../shared/frontend/dashboard/DashboardGrid";
import { DashboardConfigurationActions } from "../../../../../../../shared/frontend/dashboard/DashboardConfigurationActions";
import type { FormInstance } from "../../../../../../../shared/frontend/ui";
import type { Feedback } from "../../../../../../../shared/frontend/feedback/useFeedback";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import React from "react";
import { useNavigate } from "react-router-dom";
import type { ApiClient } from "../../../app/api/client";
import type { StudioWithAccess } from "../../../app/api/types";
import { Permissions } from "../../../app/constants/permissions";
import { spacing, spacing as designSpacing } from "../../../app/design-system";
import { formatLastDurationLabel } from "../../../app/i18n/durationLabels";
import type { I18nContextValue } from "../../../app/i18n/useI18n";
import { authQueryScope } from "../../../app/query/authQueryScope";
import { globalKey } from "../../../app/query/queryKeys";
import { formatNumber } from "../../../app/units/format";
import type { UnitsSettings } from "../../../app/units/units";
import { LoadingOverlay } from "../../../components/LoadingOverlay";
import type { SettingsListActionRef } from "../../../components/SettingsListPage";
import { StatCardContent } from "../../../components/StatCardContent";
import type { PersonDto } from "../types";
import { PersonAccessMediaOverviewSection } from "./PersonAccessMediaOverviewSection";
import { PersonOverviewMembershipBody } from "./PersonOverviewMembership";
import { PersonOverviewSituation } from "./PersonOverviewSituation";
import { PersonOverviewTodoList } from "./PersonOverviewTodoList";
import { PersonReaderActionsTable } from "./PersonReaderActionsTable";
import { setManualCheckInBlock } from "./personAccessActions";
import { ATTENDANCE_CHART_DAYS, DAYS_PER_MONTH_TO_WEEK_FACTOR, formatAttendanceDuration, usePersonCheckIns } from "./personCheckInData";
import { invalidatePersonCheckInEligibility, PERSON_DETAIL_PAYMENTS_GC_TIME_MS, PERSON_DETAIL_PAYMENTS_STALE_TIME_MS, personDetailQueryKeys } from "./personDetailQueryKeys";
import type { OpenItemRow, PaymentRow, PersonFormValues } from "./personDetailTypes";
import type { PersonDigitalFileTimelineRow } from "./personDigitalFileTimelineUtils";
import { normalizePersonDetailDto } from "./usePersonDetailRecord";
import { usePersonOverview } from "./usePersonOverview";
import { useFinanceOperation } from "../../../app/operations/useFinanceOperation";
import { PersonMemberCareActions, PersonMemberCareBody } from "./overview/PersonMemberCareWidget";
import { PersonAttendanceTrend, PersonOpenItemsList, PersonTimelineList } from "./overview/PersonOverviewWidgetBodies";
import { PersonCheckInSessionsTable, PersonRecentPaymentsTable } from "./overview/PersonOverviewTables";
import { useMemberCareAccess } from "./overview/personOverviewCareData";
import { usePersonInternalNotes } from "./overview/personOverviewNotes";
import {
  PERSON_OVERVIEW_DEFAULT_LIMIT,
  PERSON_OVERVIEW_LIMIT_OPTIONS,
  PERSON_OVERVIEW_LIMIT_WIDGETS,
  PERSON_OVERVIEW_LIST_WIDGETS,
  PERSON_OVERVIEW_WIDGET_KEYS,
  type PersonOverviewWidgetKey
} from "./overview/personOverviewWidgets";
import type { usePersonOverviewLayout } from "./overview/usePersonOverviewLayout";
import "./personOverview.css";

const LAST_WEEK_DAYS = 7;

const NOTES_MIN_ROWS = 4;
const NOTES_MAX_ROWS = 16;
const ALL_WIDGET_KEYS: string[] = [...PERSON_OVERVIEW_WIDGET_KEYS];
const ATTENDANCE_WIDGET_KEYS: PersonOverviewWidgetKey[] = ["checkins7", "checkins30", "checkinsPerWeek", "checkinsTotal", "attendanceTrend", "checkinSessions", "readerActions"];

/**
 * Der Übersichtsreiter der Personenakte als Baukasten wie das Dashboard (Plan
 * `plans/2026-10-22-personenakte-uebersicht-baukasten.md`): dasselbe Raster (`DashboardGrid`), dasselbe
 * Zahnrad, Anordnung je Benutzer (`usePersonOverviewLayout`). Karten einer Zeile teilen sich die Breite;
 * ein Block fehlt nur, wenn der Benutzer ihn ausblendet oder Recht bzw. Modul fehlen — ohne Daten zeigt er
 * seinen Leerzustand, es entstehen keine Löcher.
 *
 * Lagebild, Handlungsbedarf und Mitgliedschaft kommen aus `usePersonOverview`, Kennzahlen, Verlauf und
 * Sitzungen aus `usePersonCheckIns`; die letzten Zahlungen teilen ihren Cache mit dem Zahlungsreiter.
 */

export type PersonOverviewTabProps = {
  personId: string;
  person: PersonDto | null;
  auth: {
    api: ApiClient;
    info?: { accountId?: string } | null;
    hasAnyPermission: (...permissions: string[]) => boolean;
  };
  form: FormInstance<PersonFormValues>;
  text: I18nContextValue["text"];
  units: UnitsSettings;
  modal: ReturnType<typeof AntdApp.useApp>["modal"];
  message: Feedback;
  saving: boolean;
  allowedStudios: StudioWithAccess[];
  openItemsStudioIdsParam: string;
  paymentMethodNameByType: Record<number, string>;
  canReadContracts: boolean;
  canReadFinance: boolean;
  canReadOpenItems: boolean;
  canReadPayments: boolean;
  canReadCheckins: boolean;
  canReadAccessMedia: boolean;
  canWriteAccessMedia: boolean;
  canWritePerson: boolean;
  canBlockCheckIn: boolean;
  accessMediaRef: React.RefObject<SettingsListActionRef | null>;
  accessMediaColumnEditorModal: React.ReactNode;
  changeHomeStudio: (homeStudioId: string, options?: { refreshPerson?: boolean }) => Promise<PersonDto | null>;
  refreshPerson: () => Promise<PersonDto>;
  onOpenTab: (tab: string) => void;
  onOpenMemberAppRequest: (requestId: string) => void;
  onAddAccessMedia: () => void;
  /** Anordnung der Blöcke; liegt bei der Akte, weil das Zahnrad in der Reiterleiste sitzt. */
  layout: PersonOverviewLayout;
};

export type PersonOverviewLayout = ReturnType<typeof usePersonOverviewLayout>;

/**
 * Zahnrad bzw. „abbrechen“/„Layout speichern“ der Übersicht. Steht in der Reiterleiste der Akte, solange
 * die Übersicht offen ist — keine eigene Zeile über dem Raster.
 */
export function PersonOverviewLayoutActions({ layout, text }: { layout: PersonOverviewLayout; text: I18nContextValue["text"] }) {
  return (
    <DashboardConfigurationActions
      editing={layout.configMode}
      saving={layout.saving}
      disabled={!layout.ready}
      labels={{ configure: text("Person overview configure layout"), cancel: text("Cancel"), save: text("Save layout") }}
      onBegin={layout.begin}
      onCancel={() => { layout.cancel(); }}
      onSave={() => { return layout.save(); }}
    />
  );
}

export function PersonOverviewTab({
  personId,
  person,
  auth,
  form,
  text,
  units,
  modal,
  message,
  saving,
  allowedStudios,
  openItemsStudioIdsParam,
  paymentMethodNameByType,
  canReadContracts,
  canReadFinance,
  canReadOpenItems,
  canReadPayments,
  canReadCheckins,
  canReadAccessMedia,
  canWriteAccessMedia,
  canWritePerson,
  canBlockCheckIn,
  accessMediaRef,
  accessMediaColumnEditorModal,
  changeHomeStudio,
  refreshPerson,
  onOpenTab,
  onOpenMemberAppRequest,
  onAddAccessMedia,
  layout
}: PersonOverviewTabProps) {
  const nav = useNavigate();
  const queryClient = useQueryClient();
  const authScope = authQueryScope(auth.api, auth.info ?? null).join(":");
  const enabled = Boolean(personId && person && String(person.id) === personId);

  const overviewQuery = usePersonOverview({
    api: auth.api,
    authInfo: auth.info ?? null,
    personId,
    studioIdsParam: openItemsStudioIdsParam,
    enabled
  });
  const overview = overviewQuery.data;
  const financeOperation = { ...useFinanceOperation(personId), retryLabel: text("Retry"), errorLabel: text("Error loading data") };

  // ---------- Anordnung und Rechte der Blöcke ----------
  const { widgetStates, setWidgetStates, widgetOrder, setWidgetOrder, configMode, isWidgetVisible, setWidgetVisible } = layout;
  const limitOf = (key: string) => widgetStates[key]?.limit ?? PERSON_OVERVIEW_DEFAULT_LIMIT;

  // Gleiche Oberfläche für Mitglieder und Interessentinnen: nichts wird nach Personenart ausgeblendet, nur nach Recht.
  const showAttendance = canReadCheckins;
  const memberCareAccess = useMemberCareAccess();
  const canReadTimeline = auth.hasAnyPermission(Permissions.PersonsDocumentsRead);
  const allowedWidgetKeys = React.useMemo(() => {
    const allowed = new Set<string>(["situation", "todos", "notes"]);
    if (showAttendance) ATTENDANCE_WIDGET_KEYS.forEach((key) => allowed.add(key));
    if (canReadContracts || canReadFinance) allowed.add("membership");
    if (canReadPayments) allowed.add("payments");
    if (canReadAccessMedia) allowed.add("accessMedia");
    if (memberCareAccess.canRead) allowed.add("memberCare");
    if (canReadOpenItems) allowed.add("openItems");
    if (canReadTimeline) allowed.add("timeline");
    return allowed;
  }, [canReadAccessMedia, canReadContracts, canReadFinance, canReadOpenItems, canReadPayments, canReadTimeline, memberCareAccess.canRead, showAttendance]);
  /** Daten nur für Blöcke laden, die zu sehen sind — im Layoutmodus auch für ausgeblendete. */
  const wants = (key: PersonOverviewWidgetKey) => layout.ready && allowedWidgetKeys.has(key) && (configMode || isWidgetVisible(key));

  // ---------- Daten der Blöcke ----------
  const checkIns = usePersonCheckIns(auth.api, personId, enabled && ATTENDANCE_WIDGET_KEYS.some(wants));
  const paymentsQuery = useQuery({
    queryKey: [...personDetailQueryKeys.person.overview(personId), "payments", authScope],
    enabled: enabled && wants("payments"),
    queryFn: () => queryClient.fetchQuery({
      queryKey: [...personDetailQueryKeys.person.payments(personId), authScope],
      queryFn: () => auth.api.get<PaymentRow[]>(`/api/v1/persons/${encodeURIComponent(personId)}/payments?groupTransactions=true`, { suppressGlobalErrorToast: true }),
      staleTime: PERSON_DETAIL_PAYMENTS_STALE_TIME_MS, gcTime: PERSON_DETAIL_PAYMENTS_GC_TIME_MS
    }),
    retry: false
  });
  const openItemsQuery = useQuery({
    queryKey: [...personDetailQueryKeys.person.overview(personId), "open-items-list", openItemsStudioIdsParam, authScope],
    enabled: enabled && wants("openItems"),
    retry: false,
    queryFn: () => {
      const query = new URLSearchParams({ personId });
      if (openItemsStudioIdsParam) query.set("studioIds", openItemsStudioIdsParam);
      return auth.api.get<OpenItemRow[]>(`/api/v1/open-items?${query.toString()}`, { suppressGlobalErrorToast: true });
    }
  });
  const timelineLimit = limitOf("timeline");
  const timelineQuery = useQuery({
    queryKey: [...personDetailQueryKeys.person.overview(personId), "timeline", timelineLimit, authScope],
    enabled: enabled && wants("timeline"),
    retry: false,
    queryFn: () => auth.api.get<{ total: number; data: PersonDigitalFileTimelineRow[] }>(
      `/api/v1/persons/${encodeURIComponent(personId)}/timeline?page=1&pageSize=${timelineLimit}`, { suppressGlobalErrorToast: true })
  });

  // Stammstudio, Zutrittssperre und Notizen werden in dieser Ansicht selbst geändert. Die Antwort
  // der jeweiligen Aktion ist der neue Stand und geht direkt in den **Personen**-Cache der Seite —
  // sonst zeigt der Seitenkopf nach einer Änderung noch den alten Stand. Lagebild und
  // Handlungsbedarf leiten sich daraus ab und werden danach neu geladen.
  const personKey = React.useMemo(() => globalKey("persons", personId), [personId]);
  const setOverviewPerson = React.useCallback(
    (update: PersonDto | null | ((previous: PersonDto | null) => PersonDto | null)) => {
      queryClient.setQueryData<PersonDto | undefined>(personKey, (current) => {
        const next = typeof update === "function" ? update(current ?? null) : update;
        // Über denselben Normalisierer wie der Ladepfad: Der Cache speist auch das Profilformular.
        return next ? normalizePersonDetailDto(next) : current;
      });
    },
    [personKey, queryClient]
  );
  const invalidateOverview = React.useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: personDetailQueryKeys.person.overview(personId) });
  }, [personId, queryClient]);

  const notesSaved = React.useCallback(
    (value: string | null) => setOverviewPerson((prev) => (prev ? { ...prev, internalNotes: value } : null)),
    [setOverviewPerson]
  );
  const notesSuccess = React.useCallback(() => message.success(text("Saved.")), [message, text]);
  const notes = usePersonInternalNotes({ personId, internalNotes: person?.internalNotes, api: auth.api, onSaved: notesSaved, onSuccess: notesSuccess });

  const canChangeHomeStudio = auth.hasAnyPermission(Permissions.PersonsChangeHomeStudio);
  const changeStudio = async (newId: string) => {
    if (!person) return;
    const studioName = allowedStudios.find((s) => String(s.id) === newId)?.name || "Studio";
    const ok = await new Promise<boolean>((resolve) => {
      modal.confirm({
        title: text("Change home studio?"),
        content: text("Change home studio to").replace("{0}", studioName),
        okText: text("Yes"),
        cancelText: text("No"),
        onOk: () => resolve(true),
        onCancel: () => resolve(false)
      });
    });
    if (!ok) return;
    const updatedPerson = await changeHomeStudio(newId, { refreshPerson: true });
    if (updatedPerson) {
      message.success(text("Saved."));
      setOverviewPerson(updatedPerson);
      form.setFieldsValue({ homeStudioId: newId });
      invalidateOverview();
    }
  };

  const canControlAccess = canWritePerson || canBlockCheckIn;
  const manualBlocked = person?.isManualCheckInBlocked === true;
  const toggleManualBlock = async (wantBlock: boolean) => {
    try {
      await setManualCheckInBlock({
        api: auth.api,
        modal,
        message,
        personId,
        wantBlock,
        text,
        refreshPerson,
        onPersonUpdated: setOverviewPerson
      });
      // Die Sperre sticht den Zutrittszustand — Markierung am Foto und Lagebild neu bewerten lassen.
      invalidatePersonCheckInEligibility(queryClient, personId);
      invalidateOverview();
    } catch {
      // Central API toast shows error
    }
  };

  // ---------- Bausteine der Blöcke ----------
  // Zutrittsmedien stehen in der Mitgliedschaft, solange sie keinen eigenen Block haben.
  const accessMediaInMembership = canReadAccessMedia && !isWidgetVisible("accessMedia");
  const [accessMediaCount, setAccessMediaCount] = React.useState<number | null>(null);
  const renderAccessMediaSection = (asMembershipKpi: boolean) => (
    <PersonAccessMediaOverviewSection
      api={auth.api}
      personId={personId}
      text={text}
      canWriteAccessMedia={canWriteAccessMedia}
      accessMediaRef={accessMediaRef}
      modal={modal}
      message={message}
      onAdd={onAddAccessMedia}
      showAddButton={false}
      onCountChange={asMembershipKpi ? setAccessMediaCount : undefined}
      hideEmpty={asMembershipKpi}
    />
  );
  const addAccessMediumButton = canReadAccessMedia && canWriteAccessMedia
    ? <Button size="small" icon={<PlusOutlined />} onClick={onAddAccessMedia}>{text("Person overview add access medium")}</Button>
    : null;
  const accessToggleButton = canControlAccess
    ? manualBlocked
      ? <Button size="small" icon={<UnlockOutlined />} onClick={() => toggleManualBlock(false)}>{text("Person overview unblock access")}</Button>
      : <Button size="small" icon={<LockOutlined />} onClick={() => toggleManualBlock(true)}>{text("Person overview block access")}</Button>
    : null;

  const stats = checkIns.stats;
  const statBody = (title: React.ReactNode, value: React.ReactNode, icon: React.ReactNode, secondary?: React.ReactNode) => (
    // Kein „0“ vor dem Eintreffen der Daten: der Strich ist die Ladeaussage.
    <StatCardContent icon={icon} title={title} value={checkIns.isPending ? "–" : value} secondary={checkIns.isPending ? undefined : secondary} />
  );

  const getWidgetMeta = (key: string): DashboardCardMeta => {
    switch (key as PersonOverviewWidgetKey) {
      case "situation": return { cardTitle: text("Person overview situation title"), cardIcon: <IdcardOutlined />, loading: enabled && overviewQuery.isPending };
      case "todos": return { cardTitle: text("Person overview todo title"), cardIcon: <WarningOutlined />, loading: enabled && overviewQuery.isPending };
      case "attendanceTrend": return { cardTitle: text("Person overview attendance title"), cardIcon: <FundOutlined /> };
      case "checkinSessions": return { cardTitle: text("Attendance sessions"), cardIcon: <LoginOutlined /> };
      case "readerActions": return { cardTitle: text("Other reader device actions"), cardIcon: <ScanOutlined />, loading: checkIns.isPending };
      case "membership": return {
        cardTitle: text("Person overview membership title"),
        cardIcon: <WalletOutlined />,
        loading: enabled && overviewQuery.isPending,
        cardExtra: configMode ? null : <Space size={designSpacing.xs} wrap>{accessToggleButton}{accessMediaInMembership ? addAccessMediumButton : null}</Space>
      };
      case "accessMedia": return { cardTitle: text("Access media"), cardIcon: <CreditCardOutlined />, cardExtra: configMode ? null : addAccessMediumButton };
      case "payments": return { cardTitle: text("Recent Payments"), cardIcon: <SnippetsOutlined />, cardExtra: configMode ? null : <Button size="small" icon={<UnorderedListOutlined />} onClick={() => onOpenTab("payments")}>{text("Show all")}</Button> };
      case "openItems": return { cardTitle: text("Open items"), cardIcon: <FileTextOutlined />, loading: enabled && openItemsQuery.isPending };
      case "timeline": return { cardTitle: text("Person overview timeline title"), cardIcon: <HistoryOutlined />, loading: enabled && timelineQuery.isPending };
      case "memberCare": return {
        cardTitle: text("Member care"),
        cardIcon: <HeartOutlined />,
        cardExtra: configMode || !person
          ? null
          : <PersonMemberCareActions personId={personId} personName={[person.firstName, person.lastName].filter(Boolean).join(" ")} api={auth.api} text={text} />
      };
      case "notes": return {
        cardTitle: text("Notes"),
        cardIcon: <FormOutlined />,
        cardExtra: canWritePerson && !configMode
          ? <Button size="small" type="primary" icon={<SaveOutlined />} disabled={!notes.dirty} loading={notes.saving} onClick={() => notes.save()}>{text("Save")}</Button>
          : null
      };
      // Kennzahlkacheln tragen Titel und Symbol im Inhalt wie im Dashboard.
      default: return {};
    }
  };

  const renderWidgetBody = (key: string): React.ReactNode => {
    switch (key as PersonOverviewWidgetKey) {
      case "situation":
        return (
          <PersonOverviewSituation
            overview={overview}
            operation={financeOperation}
            homeStudioId={person?.homeStudioId}
            canChangeHomeStudio={canChangeHomeStudio && !configMode}
            allowedStudios={allowedStudios}
            saving={saving}
            text={text}
            units={units}
            onChangeHomeStudio={changeStudio}
          />
        );
      case "todos":
        return (
          <PersonOverviewTodoList
            variant="chips"
            todos={overview?.todos ?? []}
            text={text}
            units={units}
            canUnblockAccess={canControlAccess && manualBlocked && !configMode}
            onOpenTab={onOpenTab}
            onOpenAppRequest={onOpenMemberAppRequest}
            onOpenCareCase={(id) => nav(`/member-care/${encodeURIComponent(id)}`)}
            onUnblockAccess={() => void toggleManualBlock(false)}
          />
        );
      case "checkins7":
        return statBody(formatLastDurationLabel({ count: LAST_WEEK_DAYS, unit: "day", text }), formatNumber(stats.last7Days, units), <RiseOutlined />,
          stats.avgDuration7 > 0 ? `Ø ${formatAttendanceDuration(stats.avgDuration7)}` : undefined);
      case "checkins30":
        return statBody(formatLastDurationLabel({ count: ATTENDANCE_CHART_DAYS, unit: "day", text }), formatNumber(stats.last30Days, units), <LoginOutlined />,
          stats.avgDuration30 > 0 ? `Ø ${formatAttendanceDuration(stats.avgDuration30)}` : undefined);
      case "checkinsPerWeek":
        return statBody(text("Avg. per week"), formatNumber(stats.last30Days / DAYS_PER_MONTH_TO_WEEK_FACTOR, units, { minimumFractionDigits: 1, maximumFractionDigits: 1 }), <ClockCircleOutlined />);
      case "checkinsTotal":
        return statBody(text("Person overview check-ins total"), formatNumber(stats.total, units), <CheckCircleOutlined />);
      case "attendanceTrend":
        return <PersonAttendanceTrend sessions={checkIns.sessions} stats={checkIns.stats} loading={checkIns.isPending} text={text} units={units} />;
      case "checkinSessions":
        return <PersonCheckInSessionsTable sessions={checkIns.sessions} loading={checkIns.isPending} allowedStudios={allowedStudios} text={text} units={units} />;
      case "readerActions":
        return (
          <PersonReaderActionsTable
            events={checkIns.readerEvents}
            allowedStudios={allowedStudios}
            canReadFinance={canReadFinance}
            text={text}
            units={units}
            onOpenWallets={() => onOpenTab("wallets")}
          />
        );
      case "membership":
        return (
          <PersonOverviewMembershipBody
            overview={overview}
            operation={financeOperation}
            text={text}
            units={units}
            accessMedia={accessMediaInMembership ? { count: accessMediaCount, list: renderAccessMediaSection(true) } : undefined}
          />
        );
      case "accessMedia":
        return renderAccessMediaSection(false);
      case "payments":
        return (
          <PersonRecentPaymentsTable
            payments={paymentsQuery.data ?? []}
            loading={enabled && paymentsQuery.isPending}
            error={paymentsQuery.isError && !paymentsQuery.data}
            accountId={auth.info?.accountId}
            text={text}
            units={units}
            paymentMethodNameByType={paymentMethodNameByType}
          />
        );
      case "openItems":
        return <PersonOpenItemsList items={openItemsQuery.data ?? []} limit={limitOf("openItems")} error={openItemsQuery.isError} text={text} units={units} />;
      case "timeline":
        return <PersonTimelineList rows={timelineQuery.data?.data ?? []} error={timelineQuery.isError} text={text} units={units} />;
      case "memberCare":
        return <PersonMemberCareBody personId={personId} api={auth.api} text={text} units={units} />;
      case "notes":
        return (
          <Input.TextArea
            readOnly={!canWritePerson || configMode}
            value={notes.value}
            onChange={(event) => { if (canWritePerson) notes.setValue(event.target.value); }}
            autoSize={{ minRows: NOTES_MIN_ROWS, maxRows: NOTES_MAX_ROWS }}
            disabled={notes.saving}
            aria-label={text("Notes")}
          />
        );
      default:
        return null;
    }
  };

  // Zeilenanzahl der Listen: im Layoutmodus neben dem Kartentitel wählbar, gespeichert mit der Anordnung.
  const renderLimitSelector = (key: string) => PERSON_OVERVIEW_LIMIT_WIDGETS.has(key) ? (
    <Select
      size="small"
      aria-label={text("Person overview rows per list")}
      value={limitOf(key)}
      options={PERSON_OVERVIEW_LIMIT_OPTIONS.map((value) => ({ value, label: text("Person overview rows count").replace("{0}", String(value)) }))}
      onChange={(value) => setWidgetStates((previous) => {
        const widget = previous[key];
        return widget ? { ...previous, [key]: { ...widget, limit: Number(value) } } : previous;
      })}
    />
  ) : null;

  const widgetTarget = (key: string): (() => void) | undefined => {
    if (configMode) return undefined;
    switch (key as PersonOverviewWidgetKey) {
      case "openItems": return () => onOpenTab("openitems");
      case "timeline": return () => onOpenTab("files");
      default: return undefined;
    }
  };

  return (
    <div className="person-overview">
      {overviewQuery.isError ? (
        <Alert type="error" showIcon message={text("Error loading data")} action={<Button onClick={() => overviewQuery.refetch()}>{text("Retry")}</Button>} style={{ marginBottom: spacing.lg }} />
      ) : null}
      {!layout.ready ? <LoadingOverlay visible scope="placeholder" /> : (
        <DashboardGrid
          disabled={layout.saving}
          controls={{ show: text("Show"), drag: text("Reorder") }}
          allWidgetKeys={ALL_WIDGET_KEYS}
          allowedWidgetKeys={allowedWidgetKeys}
          widgetOrder={widgetOrder}
          setWidgetOrder={setWidgetOrder}
          configMode={configMode}
          isWidgetVisible={isWidgetVisible}
          setWidgetVisible={setWidgetVisible}
          listWidgetKeys={PERSON_OVERVIEW_LIST_WIDGETS}
          getWidgetMeta={getWidgetMeta}
          renderWidgetBody={renderWidgetBody}
          renderPeriodSelector={(key) => (configMode ? renderLimitSelector(key) : null)}
          widgetTarget={widgetTarget}
        />
      )}
      {accessMediaColumnEditorModal}
    </div>
  );
}
